import { useState } from 'react'
import { useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Settings, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Loader2,
  AlertTriangle 
} from 'lucide-react'

export default function PrimoUpDebug() {
  const [loading, setLoading] = useState(false)
  const [envCheck, setEnvCheck] = useState<any>(null)
  const [setupResult, setSetupResult] = useState<any>(null)
  const [testResult, setTestResult] = useState<any>(null)

  const checkEnvironment = useAction(api.primoup.checkEnvironmentVariables)
  const setupConnection = useAction(api.primoup.setupConnection)
  const testConnection = useAction(api.primoup.testConnection)

  const handleCheckEnvironment = async () => {
    setLoading(true)
    try {
      const result = await checkEnvironment()
      setEnvCheck(result)
    } catch (error: any) {
      console.error('❌ Environment check failed:', error)
      setEnvCheck({ error: error.message })
    } finally {
      setLoading(false)
    }
  }

  const handleSetupConnection = async () => {
    setLoading(true)
    try {
      const result = await setupConnection()
      setSetupResult(result)
    } catch (error: any) {
      console.error('❌ Setup failed:', error)
      setSetupResult({ success: false, message: error.message })
    } finally {
      setLoading(false)
    }
  }

  const handleTestConnection = async () => {
    setLoading(true)
    try {
      const result = await testConnection()
      setTestResult(result)
      console.log('🧪 Test result:', result)
    } catch (error: any) {
      console.error('❌ Test failed:', error)
      setTestResult({ success: false, message: error.message })
    } finally {
      setLoading(false)
    }
  }

  const StatusBadge = ({ success, label }: { success?: boolean, label: string }) => {
    if (success === undefined) return <Badge variant="outline">{label}</Badge>
    return (
      <Badge className={success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
        {success ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        {label}
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>PrimoUp Debug Panel</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Environment Check */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Environment Variables</h3>
              <Button 
                onClick={handleCheckEnvironment} 
                disabled={loading}
                variant="outline"
                size="sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Check
              </Button>
            </div>
            
            {envCheck && (
              <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded">
                <StatusBadge success={envCheck.hasBaseUrl} label="Base URL" />
                <StatusBadge success={envCheck.hasEmail} label="Email" />
                <StatusBadge success={envCheck.hasPassword} label="Password" />
                <div className="col-span-2 text-xs text-gray-600 mt-2">
                  {envCheck.baseUrl && <div>URL: {envCheck.baseUrl}</div>}
                  {envCheck.email && <div>Email: {envCheck.email}</div>}
                  {envCheck.error && <div className="text-red-600">Error: {envCheck.error}</div>}
                </div>
              </div>
            )}
          </div>

          {/* Setup Connection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Setup Connection</h3>
              <Button 
                onClick={handleSetupConnection} 
                disabled={loading}
                variant="outline"
                size="sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Setup
              </Button>
            </div>
            
            {setupResult && (
              <div className="p-3 bg-gray-50 rounded">
                <StatusBadge success={setupResult.success} label={setupResult.success ? "Connected" : "Failed"} />
                <div className="text-xs text-gray-600 mt-2">
                  {setupResult.message}
                  {setupResult.userInfo && (
                    <div className="mt-1">
                      User: {setupResult.userInfo.name || setupResult.userInfo.email}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Test Connection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Test Connection</h3>
              <Button 
                onClick={handleTestConnection} 
                disabled={loading}
                variant="outline"
                size="sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Test
              </Button>
            </div>
            
            {testResult && (
              <div className="p-3 bg-gray-50 rounded">
                <StatusBadge success={testResult.success} label={testResult.success ? "Working" : "Failed"} />
                <div className="text-xs text-gray-600 mt-2">
                  {testResult.message}
                  {testResult.data && (
                    <pre className="mt-1 text-xs bg-white p-2 rounded border overflow-auto max-h-32">
                      {JSON.stringify(testResult.data, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="p-3 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <div className="font-medium mb-1">Setup Instructions:</div>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Configure PrimoUp environment variables in Convex dashboard</li>
                  <li>Set PRIMOUP_API_BASE_URL (e.g., https://staging.primoup.it)</li>
                  <li>Set PRIMOUP_EMAIL (your PrimoUp login email)</li>
                  <li>Set PRIMOUP_PASSWORD (your PrimoUp login password)</li>
                  <li>Click "Setup" to establish connection</li>
                  <li>Click "Test" to verify the connection works</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}


